/**
 * Assemble the frozen input set a report runs over.
 *
 * `reporting` may not fetch - no store, no clock, no I/O - and `events/asset-register` returns rows
 * rather than assembling them. This is the one function allowed to do both: pull the ledger for a
 * set of assets, put it beside the asset rows, and stamp the result with something that identifies
 * the inputs.
 *
 * ## Why `snapshotId` exists
 *
 * `AR-RPT-03` wants a report to be reproducible. `generatedAt` alone does not do it: two runs
 * seconds apart over a live store can differ with nothing in the output to show it. The id is a
 * digest of what actually went in - which assets, which events, which period - so two artefacts
 * carrying the same id were built from the same rows, and two carrying different ids were not.
 *
 * It is deliberately **not** random and **not** a timestamp. Same inputs, same id, on any machine.
 */

import i18next from 'i18next'

import { fetchAssetEventsForAssets } from 'helper-functions/events/asset-register/asset-event-crud'
import { withoutReversed } from 'helper-functions/events/asset-register/ledger'

import { toIsoDate, toQueryWindow } from './period'

/** The ceiling passed to the events query. The API offers no cursor; see `truncated` below. */
export const SNAPSHOT_MAX_EVENTS = 5000

/**
 * FNV-1a, 32-bit, hex. Small, dependency-free and stable across machines - which is all the id
 * needs to be. It identifies an input set; it does not defend against anything.
 */
const digest = input => {
    let hash = 0x811c9dc5
    for (let i = 0; i < input.length; i += 1) {
        hash ^= input.charCodeAt(i)
        hash = Math.imul(hash, 0x01000193) >>> 0
    }
    return hash.toString(16).padStart(8, '0')
}

/**
 * Derived from the ids of what went in, not from the moment it was read.
 *
 * Event ids are included rather than just counted: a corrected event that replaces another leaves
 * the count identical and the content different, and a digest that cannot tell those apart is worse
 * than no digest.
 */
export const snapshotIdFor = ({ assets = [], events = [], periodFrom, periodTo, asAt } = {}) => {
    const assetIds = assets
        .map(asset => asset?.id)
        .filter(id => id != null)
        .sort()
    const eventIds = events
        .map(event => event?.id ?? `${event?.type}@${event?.time}`)
        .filter(Boolean)
        .sort()
    return `snap-${digest(JSON.stringify([assetIds, eventIds, toIsoDate(periodFrom), toIsoDate(periodTo), toIsoDate(asAt)]))}`
}

/**
 * The events API answers `{ items: [...] }`. Nothing in the register said so, and a reader that
 * shrugs at an unrecognised shape returns an empty ledger and reports success - which is the exact
 * failure this module exists to prevent. A live run against 119 assets came back with zero events
 * and `ledgerRead: true` before this threw instead.
 *
 * So: known shapes are unwrapped, and anything else is an error rather than an empty list.
 */
export const unwrapEvents = response => {
    if (response == null) return []
    if (Array.isArray(response)) return response
    if (Array.isArray(response.items)) return response.items
    if (Array.isArray(response.data)) return response.data
    if (Array.isArray(response.events)) return response.events
    throw new Error(`the events API answered a shape this reader does not know: ${Object.keys(response).join(', ') || typeof response}`)
}

/**
 * Pull the ledger for a set of assets and return the snapshot a report runs over.
 *
 * The asset rows are supplied by the caller rather than fetched here, because the app already holds
 * every asset in redux and a second read would be a different set from the one on screen.
 *
 * **The period is applied to the query, not only to the runner.** A report over one financial year
 * has no business pulling ten years of ledger. Where a runner needs history either side of the
 * period - a replay does - pass `fullHistory` and filter in the runner instead.
 *
 * @param {{
 *   assets: Object[],
 *   farms?: Object[],
 *   cohorts?: Object[]  livestock cohorts, for the chemical kinds that need an animal's record
 *   tenant?: string|number,
 *   periodFrom?: string, periodTo?: string, asAt?: string,
 *   types?: string[],
 *   fullHistory?: boolean,
 *   maxResults?: number,
 *   fetchEvents?: Function  the reader, injected so this is testable and offline-swappable
 * }} input
 * @returns {Promise<import('helper-functions/reporting/report-contract').ReportSnapshot>}
 */
export const assembleSnapshot = async ({
    assets = [],
    paddocks = [],
    farms = [],
    cohorts,
    tenant,
    periodFrom,
    periodTo,
    asAt,
    types,
    fullHistory = false,
    includeFake = false,
    maxResults = SNAPSHOT_MAX_EVENTS,
    fetchEvents = fetchAssetEventsForAssets,
} = {}) => {
    const notes = []

    /**
     * Demo rows do not go on an accountant's schedule.
     *
     * `detail.isFake` marks seeded demo data. The map draws it differently and `asset-edit.js`
     * keeps fake paddocks away from real assets, but nothing in reporting knew about it - so a
     * register listing counted demo rows in its class subtotals, the compliance report raised
     * obligations against them, and the insurance schedule would have put one on a document going
     * to an insurer. On the live tenant one of them is called "Fake data distribution board sub".
     *
     * MockData generates these rows in the browser to fill empty demo views. They never reach the
     * backend. A report built on them would put invented figures on an accountant's schedule.
     *
     * Excluded by default because the failure is one-directional: a real asset missing from a report
     * is a gap someone notices, and a fake one present is a figure someone acts on. `includeFake`
     * exists so a demo can opt back in deliberately; nothing in the register passes it today.
     *
     * The count is always reported. A report that quietly drops rows is the thing this module
     * spends its whole time not being.
     */
    const realAssets = includeFake ? assets : assets.filter(asset => !asset?.detail?.isFake)
    const realPaddocks = includeFake ? paddocks : paddocks.filter(paddock => !paddock?.detail?.isFake)
    const fakeAssets = assets.length - realAssets.length
    const fakePaddocks = paddocks.length - realPaddocks.length

    if (fakeAssets || fakePaddocks) {
        const parts = []
        if (fakeAssets) parts.push(i18next.t('10990', { count: fakeAssets }))
        if (fakePaddocks) parts.push(i18next.t('10991', { count: fakePaddocks }))
        notes.push(`${parts.join(' and ')} marked as demo data (detail.isFake) were left out of this run. They are excluded so that seeded rows cannot reach a document an accountant or an insurer reads.`)
    }

    assets = realAssets
    paddocks = realPaddocks
    const assetIds = assets.map(asset => asset?.id).filter(id => id != null)

    if (!assetIds.length) {
        // Same shape as the full return, deliberately. `paddocks` and `ledgerRead` were missing
        // here, so an empty tenant handed runners a snapshot that was a different type from every
        // other snapshot - and `ledgerRead: undefined` is not `false`, which is the one distinction
        // the runners' "the ledger could not be read" note depends on.
        return { assets: [], paddocks, farms, ...(cohorts ? { cohorts } : {}), events: [], snapshotId: snapshotIdFor({ periodFrom, periodTo, asAt }), truncated: false, ledgerRead: true, notes: [...notes, i18next.t('10992')] }
    }

    // A replay has to start before the window: an asset acquired in 2019 and still held is not
    // described by any event inside FY2026.
    const window = fullHistory || asAt ? {} : toQueryWindow({ from: periodFrom, to: periodTo })

    let events = []
    let failed = null
    try {
        // No `assetIds`: the register holds every asset for the tenant, so listing them was the
        // whole tenant written into a query string. See `fetchAssetEventsForAssets`.
        const response = await fetchEvents({ tenant, types, maxResults, ...window })
        // AR-MOD-02: a reversed event and its reversal state nothing a figure can be computed from.
        events = withoutReversed(unwrapEvents(response))
    } catch (err) {
        failed = err?.message || String(err)
    }

    if (failed) {
        // A report built on a silently empty ledger is the failure this module exists to prevent.
        // The snapshot is still returned - the caller decides - but it cannot claim to hold history.
        notes.push(i18next.t('10993'))
    }

    const truncated = events.length >= maxResults
    if (truncated) {
        notes.push(i18next.t('10994', { maxResults }))
    }

    return {
        assets,
        // `ReportSnapshot` declares them and nothing was filling them in, so every paddock lookup in
        // a runner missed and an asset located only by `paddockId` read as having no location.
        paddocks,
        // Same story, and `CR-STK-16` is the requirement that needs them: a chemical store's PIC is
        // `farm.picNumber` reached through the store's `farmId`, so a report without the farms cannot
        // walk that chain. It is passed through rather than fetched, for the reason the assets are.
        farms,
        // Livestock cohorts, passed through like the farms. An animal is its cohort (`aIds` names the
        // animals it holds), and the withholding engine's dairy gate reads `detail.production.usage` off
        // it. Absent means "not supplied", which a runner must say, not "no animals".
        ...(cohorts ? { cohorts } : {}),
        events,
        snapshotId: snapshotIdFor({ assets, events, periodFrom, periodTo, asAt }),
        truncated,
        ledgerRead: !failed,
        notes,
    }
}

export default assembleSnapshot
